import { TIMEOUT } from '@shared-core/features/notifications'
import { useNotifications } from '@shared-core/features/notifications/context'
import { useEvent } from '@shared-core/hooks/use-event'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { useHaptic } from '@shared-ui/platform/hooks/use-haptic'
import { isTMA, isTMAAndroid, readTmaClipboardText } from '@web/libs/tg/platform'
import locales from './use-clipboard.i18n.json'

// TMA/Safari часто блокирует navigator.clipboard — фолбэк через textarea + execCommand.
function writeViaExecCommand(text: string): boolean {
  if (typeof document === 'undefined') return false

  const activeElement = document.activeElement as HTMLElement | null
  const textarea = document.createElement('textarea')

  textarea.value = text
  textarea.setAttribute('readonly', '')
  // Узел обязан быть реально отрисованным и с ненулевым размером: WebView (в первую очередь
  // Android) отказывается выделять контент у 1×1 / opacity:0 элемента, а execCommand при этом
  // всё равно возвращает true — отсюда «Скопировано» при пустом буфере. Прячем цветом и z-index,
  // размеры оставляем настоящие. position: fixed — чтобы не дёргать скролл страницы.
  textarea.style.position = 'fixed'
  textarea.style.top = '0'
  textarea.style.left = '0'
  textarea.style.width = '2em'
  textarea.style.height = '2em'
  textarea.style.padding = '0'
  textarea.style.border = 'none'
  textarea.style.outline = 'none'
  textarea.style.boxShadow = 'none'
  textarea.style.background = 'transparent'
  textarea.style.color = 'transparent'
  textarea.style.zIndex = '-1'
  // 16px — иначе iOS зумит страницу при фокусе на поле ввода.
  textarea.style.fontSize = '16px'

  document.body.appendChild(textarea)
  textarea.focus({ preventScroll: true })
  textarea.select()
  textarea.setSelectionRange(0, text.length)

  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }

  document.body.removeChild(textarea)
  activeElement?.focus?.({ preventScroll: true })

  return ok
}

// execCommand в WebView Telegram возвращает true, ничего не записав. Async Clipboard API там
// обычно недоступен, поэтому «успех» фолбэка перепроверяем чтением через Telegram SDK.
// Проверка только отрицательная: прочитали другое → точно не скопировалось; прочитать не смогли
// (метод недоступен / Telegram не вызвал колбэк) → не гасим честный тост.
async function isFallbackWriteConfirmed(text: string): Promise<boolean> {
  if (!isTMA()) return true

  const clipboardText = await readTmaClipboardText()

  if (!clipboardText) return true

  return clipboardText === text
}

// Пишет в буфер: Clipboard API → textarea/execCommand. Для хука и web-shim'ов.
export async function writeToClipboard(text: string): Promise<boolean> {
  if (!text) return false

  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Clipboard API недоступен / Permissions-Policy в TMA — пробуем фолбэк ниже
  }

  if (!writeViaExecCommand(text)) return false

  return isFallbackWriteConfirmed(text)
}

async function readClipboardText(): Promise<string> {
  // Без фокуса окна Chromium кидает NotAllowedError («Document is not focused»),
  // а Ctrl+V при этом работает — отсюда «кнопка Paste мёртвая».
  if (typeof window !== 'undefined') {
    try {
      window.focus()
    } catch {
      // ignore
    }
  }

  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.readText) {
      const text = await navigator.clipboard.readText()

      if (text) return text
    }
  } catch {
    // WebView в TMA / denied clipboard-read — пробуем фолбэк ниже
  }

  if (isTMA()) return readTmaClipboardText()

  return ''
}

export function useClipboard() {
  const { t } = useI18N(locales)
  const { addToast, addToastError } = useNotifications()
  const haptic = useHaptic()

  // message — необязательный текст тоста вместо стандартного «Скопировано»
  const handleCopy = useEvent((str: string, message?: string) => () => {
    haptic('impactLight')
    void writeToClipboard(str).then((ok) => {
      // Молча ничего не делать нельзя: юзер нажал и не понимает, сработало или нет.
      if (!ok) {
        addToastError(t('copy-failed'))
        return
      }

      addToast({
        text: message ?? t('copied'),
        timeout: TIMEOUT.COPY,
        isClosable: false,
        icon: 'copy-stroke',
        override: true,
      })
    })
  })

  const getContent = useEvent(async () => readClipboardText())

  const pasteContent = useEvent(async () => {
    const text = await readClipboardText()

    if (!text) {
      addToast({ text: t('paste-empty'), timeout: TIMEOUT.COPY, isClosable: false, override: true })
      return ''
    }

    return text
  })

  // Без тоста: вызывающий сам решает, что показать. Возвращает результат записи,
  // чтобы не рапортовать «Скопировано» вслепую.
  const silent = useEvent((str: string) => writeToClipboard(str))

  // TMA Android: WebView не даёт clipboard-read, а readTextFromClipboard работает
  // только при запуске из attachment-меню — кнопки «Вставить» там не показываем.
  const isPasteAvailable = !isTMAAndroid()

  return { handleCopy, getContent, silent, isPasteAvailable, pasteContent }
}
